import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelCase, get } from 'lodash';

import {
  AssetContainerEventType,
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats
} from '@nova/core';
import { getAssetContainerStateIcon } from '@satellite/composables/yardAssetPresenterShared';
import { useYardAssetStatus } from '@satellite/composables/useYardAssetStatus';
import { nestedAppointmentPresenterDataProps } from '@satellite/composables/presenterNestedAppointmentProps';
import { nestedAssetVisitPresenterDataProps } from '@satellite/composables/presenterNestedAssetVisitProps';
import { nestedCompanyPresenterDataProps } from '@satellite/composables/presenterNestedCompanyProps';
import { nestedSpotPresenterDataProps } from '@satellite/composables/presenterNestedSpotProps';
import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { reactiveOption } from '@satellite/composables/presenterReactiveOption';
import { toPresenterPlaceholder } from '@satellite/composables/presenterPlaceholder';

import type { ComputedRef, Ref } from 'vue';
import type { ComposerTranslation } from 'vue-i18n';
import type { Maybe } from '@nova/core';
import type {
  PresenterDisplayConfigOptions,
  PresenterDisplayOverrideProps,
  PresenterDisplaySize
} from '@satellite/composables/presenterDisplayOptions';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenter';
import type { YardAssetDetails } from './yardAssetPresenterShared';

export type YardAssetDisplayField =
  | 'arrivedTime'
  | 'code'
  | 'displayStatus'
  | 'displayStatusChip'
  | 'displayStatusIcon'
  | 'dropoffCarrierCompany'
  | 'owner'
  | 'pickupCarrierCompany'
  | 'spot'
  | 'state'
  | 'stateChip'
  | 'stateIcon'
  | 'status'
  | 'statusChip'
  | 'statusIcon'
  | 'tags'
  | 'type'
  | 'typeChip'
  | 'typeIcon';

export interface UseYardAssetDisplayOptions extends PresenterDisplayConfigOptions {
  codeProps?: PresenterDisplayOverrideProps;
  arrivedTimeProps?: PresenterDisplayOverrideProps;
  displayStatusProps?: PresenterDisplayOverrideProps;
  displayStatusIconProps?: PresenterDisplayOverrideProps;
  stateProps?: PresenterDisplayOverrideProps;
  statusProps?: PresenterDisplayOverrideProps;
  statusIconProps?: PresenterDisplayOverrideProps;
  typeChipProps?: PresenterDisplayOverrideProps;
  typeIconProps?: PresenterDisplayOverrideProps;
  typeProps?: PresenterDisplayOverrideProps;
  spotPresenterProps?: PresenterDisplayOverrideProps;
  stateIconProps?: PresenterDisplayOverrideProps;
  tagsContainerProps?: PresenterDisplayOverrideProps;
}

type DisplaySize = PresenterDisplaySize;

const OUTLINED_STATUS_CHIP_STATUSES = [
  AssetContainerEventType.Arrived,
  AssetContainerEventType.InTransit,
  AssetContainerEventType.Loading,
  AssetContainerEventType.Unloading
];

function chipSizeForDisplay(size: DisplaySize) {
  return size === 'small' ? 'x-small' : 'small';
}

function resolveSpotPlaceholderFromAsset(
  asset: YardAssetDetails | null
): PresenterPlaceholderData<YardSpotDetails> | null {
  if (!asset) {
    return null;
  }

  const assignment = asset.spotAssignments?.[0] ?? asset.assetVisit?.spotAssignments?.[0];
  const spotId = assignment?.spotId ?? null;

  if (!spotId) {
    return null;
  }

  const spot = (assignment as { spot?: YardSpotDetails } | undefined)?.spot;

  return toPresenterPlaceholder<YardSpotDetails>({
    id: spotId,
    code: spot?.code ?? '',
    type: spot?.type ?? '',
    spotAreaId: spot?.spotAreaId ?? ''
  });
}

function chipTextTypographyClass(size: DisplaySize) {
  return {
    'typography-chip-sm': size === 'small',
    'typography-caption-bold': ['regular', 'large'].includes(size)
  };
}

function translateAssetContainerType(t: ComposerTranslation, type: Maybe<string>) {
  if (!type) {
    return null;
  }

  return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
}

function translateAssetContainerState(t: ComposerTranslation, state: Maybe<string>) {
  if (!state) {
    return null;
  }

  return t(`core.yard.assetContainerStates.${camelCase(state)}`, state);
}

function formatAssetDateTime(dateTime: string, warehouseTimezone: Maybe<string>) {
  return formatDateTimeWithMilitarySupport(
    dateTime,
    warehouseTimezone,
    LuxonDateTimeFormats.ShortMonthYearDayTimeAMPM,
    false,
    LuxonDateTimeFormats.ShortMonthYearDayTime24
  );
}

function resolveSpotIcon(hasAssignment: boolean) {
  return hasAssignment ? 'mdi-map-marker-outline' : 'mdi-map-marker-off-outline';
}

/** Resolve dropoff (arrived) carrier name from container data (no store). */
export function getDropoffCarrierName(assetContainer: YardAssetDetails | null) {
  if (!assetContainer) {
    return null;
  }

  const appointmentId = assetContainer.dropAppointment?.id ?? assetContainer.dropAppointmentId;
  if (appointmentId) {
    return {
      appointmentId,
      appointment: assetContainer.dropAppointment ?? null,
      assetVisitId: null,
      assetVisit: null
    };
  }

  const assetContainerEvents = Array.isArray(assetContainer.assetContainerEvents)
    ? assetContainer.assetContainerEvents
    : [];
  const arrivedEvent = assetContainerEvents.find(
    event => event.eventType === AssetContainerEventType.Arrived
  );

  if (!arrivedEvent) {
    return null;
  }

  if (arrivedEvent.assetVisitId || arrivedEvent.assetVisit?.id) {
    return {
      appointmentId: null,
      appointment: null,
      assetVisitId: arrivedEvent.assetVisitId ?? arrivedEvent.assetVisit?.id,
      assetVisit: arrivedEvent.assetVisit ?? null
    };
  }

  const assetVisitId = get(arrivedEvent, 'assetVisitId');
  if (assetVisitId) {
    return {
      appointmentId: null,
      appointment: null,
      assetVisitId,
      assetVisit: null
    };
  }

  return null;
}

export function getPickupCarrierName(assetContainer: YardAssetDetails | null) {
  if (!assetContainer) {
    return null;
  }

  const appointmentId = assetContainer.pickupAppointment?.id ?? assetContainer.pickupAppointmentId;
  if (appointmentId) {
    return {
      appointmentId,
      appointment: assetContainer.pickupAppointment ?? null,
      assetVisitId: null,
      assetVisit: null
    };
  }

  return null;
}

function useYardAssetDisplayAttrs(
  displaySize: ComputedRef<DisplaySize>,
  options: UseYardAssetDisplayOptions,
  derived: {
    statusChipVariant: ComputedRef<string>;
    statusIcon: ComputedRef<string>;
    statusColor: ComputedRef<string | undefined>;
    statusLabel: ComputedRef<string | null>;
    displayStatusChipVariant: ComputedRef<string>;
    displayStatusIcon: ComputedRef<string>;
    displayStatusColor: ComputedRef<string | undefined>;
    displayStatusLabel: ComputedRef<string | null>;
    stateIcon: ComputedRef<string>;
  }
) {
  const codeAttrs = computed(() =>
    mergeProps(
      {
        class: {
          'typography-body-sm-bold': displaySize.value === 'small',
          'typography-body-md-bold': displaySize.value === 'regular',
          'text-capitalize': true
        },
        'data-testid': 'yard-asset-presenter-asset-code'
      },
      toValue(options.codeProps) ?? {}
    )
  );

  const typeAttrs = computed(() =>
    mergeProps(
      { class: 'text-capitalize', 'data-testid': 'yard-asset-presenter-asset-type' },
      toValue(options.typeProps) ?? {}
    )
  );

  const typeChipAttrs = computed(() =>
    mergeProps(
      {
        variant: 'tonal',
        size: chipSizeForDisplay(displaySize.value),
        'data-testid': 'yard-asset-presenter-asset-type-chip'
      },
      toValue(options.typeChipProps) ?? {}
    )
  );

  const typeChipTextAttrs = computed(() => ({
    class: {
      ...chipTextTypographyClass(displaySize.value),
      'text-capitalize': true,
      'text--color-text-neutral-90': true
    },
    'data-testid': 'yard-asset-presenter-asset-type-chip-text'
  }));

  const typeIconAttrs = computed(() =>
    mergeProps(
      {
        size: 20,
        icon: 'mdi-truck-trailer',
        'data-testid': 'yard-asset-presenter-asset-type-icon'
      },
      toValue(options.typeIconProps) ?? {}
    )
  );

  const arrivedTimeAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-arrived-time' },
      toValue(options.arrivedTimeProps) ?? {}
    )
  );

  const statusAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-asset-status' },
      toValue(options.statusProps) ?? {}
    )
  );

  const statusChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    ripple: true,
    variant: derived.statusChipVariant.value,
    prependIcon: derived.statusIcon.value,
    color: derived.statusColor.value,
    'data-testid': 'yard-asset-presenter-asset-status-chip'
  }));

  const statusChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-asset-status-chip-text'
  }));

  const statusIconAttrs = computed(() =>
    mergeProps(
      {
        size: '1em',
        icon: derived.statusIcon.value,
        color: derived.statusColor.value,
        title: derived.statusLabel.value,
        'data-testid': 'yard-asset-presenter-asset-status-icon'
      },
      toValue(options.statusIconProps) ?? {}
    )
  );

  const displayStatusAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-asset-display-status' },
      toValue(options.displayStatusProps) ?? {}
    )
  );

  const displayStatusChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    ripple: true,
    variant: derived.displayStatusChipVariant.value,
    prependIcon: derived.displayStatusIcon.value,
    color: derived.displayStatusColor.value,
    'data-testid': 'yard-asset-presenter-asset-display-status-chip'
  }));

  const displayStatusChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-asset-display-status-chip-text'
  }));

  const displayStatusIconAttrs = computed(() =>
    mergeProps(
      {
        size: '1em',
        icon: derived.displayStatusIcon.value,
        color: derived.displayStatusColor.value,
        title: derived.displayStatusLabel.value,
        'data-testid': 'yard-asset-presenter-asset-display-status-icon'
      },
      toValue(options.displayStatusIconProps) ?? {}
    )
  );

  const stateAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-asset-state' },
      toValue(options.stateProps) ?? {}
    )
  );

  const stateChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    ripple: true,
    variant: 'outlined',
    prependIcon: derived.stateIcon.value,
    'data-testid': 'yard-asset-presenter-asset-state-chip'
  }));

  const stateChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-asset-state-chip-text'
  }));

  const tagChipAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-tag-chip'
  }));

  const tagsContainerAttrs = computed(() =>
    mergeProps(
      {
        class: 'd-flex flex-row gap-1 flex-wrap',
        'data-testid': 'yard-asset-presenter-tags'
      },
      toValue(options.tagsContainerProps) ?? {}
    )
  );

  const stateIconAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-state-icon' },
      toValue(options.stateIconProps) ?? {}
    )
  );

  return {
    codeAttrs,
    typeAttrs,
    typeChipAttrs,
    typeChipTextAttrs,
    typeIconAttrs,
    arrivedTimeAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    statusIconAttrs,
    displayStatusAttrs,
    displayStatusChipAttrs,
    displayStatusChipTextAttrs,
    displayStatusIconAttrs,
    stateAttrs,
    stateChipAttrs,
    stateChipTextAttrs,
    tagChipAttrs,
    tagsContainerAttrs,
    stateIconAttrs
  };
}

export function useYardAssetDisplay(
  asset: Ref<YardAssetDetails | null> | ComputedRef<YardAssetDetails | null>,
  options: UseYardAssetDisplayOptions = {}
) {
  const { t } = useI18n();
  const assetStatus = useYardAssetStatus();

  const displaySize = reactiveOption<DisplaySize>(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterMode.Live);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? null);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const code = computed(() => asset.value?.code ?? null);
  const typeRaw = computed(() => asset.value?.type ?? null);
  const typeLabel = computed(() => translateAssetContainerType(t, typeRaw.value));
  const arrivedTime = computed(() => asset.value?.createDateTime ?? null);
  const arrivedTimeFormatted = computed(() =>
    arrivedTime.value ? formatAssetDateTime(arrivedTime.value, effectiveTimezone.value) : null
  );
  const owner = computed(() => asset.value?.company ?? null);
  const state = computed(() => (asset.value?.state ? String(asset.value.state) : null));
  const tags = computed(() => asset.value?.tags ?? null);

  const status = computed(() => assetStatus.getRawStatus(asset.value));
  const displayStatus = computed(() => assetStatus.getDisplayStatus(asset.value));
  const isInTransit = computed(() => assetStatus.isAssetInTransit(asset.value));

  const statusLabel = computed(() => assetStatus.getRawStatusLabel(asset.value));
  const displayStatusLabel = computed(() => assetStatus.getDisplayStatusLabel(asset.value));
  const stateLabel = computed(() => translateAssetContainerState(t, state.value));

  const statusIcon = computed(() => assetStatus.getRawStatusIcon(asset.value));
  const displayStatusIcon = computed(() => assetStatus.getDisplayStatusIcon(asset.value));
  const statusColor = computed(
    () => assetStatus.getRawStatusColor(asset.value) ?? 'text--color-accent-60'
  );
  const displayStatusColor = computed(
    () => assetStatus.getDisplayStatusColor(asset.value) ?? 'text--color-accent-60'
  );
  const chipVariantForStatus = (statusValue: Maybe<string>) =>
    OUTLINED_STATUS_CHIP_STATUSES.includes(statusValue as AssetContainerEventType)
      ? 'outlined'
      : 'tonal';
  const statusChipVariant = computed(() => chipVariantForStatus(status.value));
  const displayStatusChipVariant = computed(() => chipVariantForStatus(displayStatus.value));
  const stateIcon = computed(() => getAssetContainerStateIcon(asset.value));

  const spotId = computed(
    () =>
      asset.value?.spotAssignments?.[0]?.spotId ??
      asset.value?.assetVisit?.spotAssignments?.[0]?.spotId ??
      null
  );
  const hasAssignment = computed(() => Boolean(spotId.value));
  const spotIcon = computed(() => resolveSpotIcon(hasAssignment.value));

  const dropoffCarrier = computed(() => getDropoffCarrierName(asset.value));
  const pickupCarrier = computed(() => getPickupCarrierName(asset.value));

  const ownerPresenterProps = computed(() =>
    nestedCompanyPresenterDataProps(displayMode.value, { company: owner.value })
  );
  const spotPlaceholder = computed(() => resolveSpotPlaceholderFromAsset(asset.value));
  const spotPresenterPropsMerged = computed(() =>
    mergeProps(
      nestedSpotPresenterDataProps(displayMode.value, {
        spot: spotPlaceholder.value,
        spotArea: null
      }),
      toValue(options.spotPresenterProps) ?? {}
    )
  );
  const dropoffAppointmentPresenterProps = computed(() =>
    nestedAppointmentPresenterDataProps(displayMode.value, {
      appointment: dropoffCarrier.value?.appointment ?? null
    })
  );
  const pickupAppointmentPresenterProps = computed(() =>
    nestedAppointmentPresenterDataProps(displayMode.value, {
      appointment: pickupCarrier.value?.appointment ?? null
    })
  );
  const dropoffAssetVisitPresenterProps = computed(() =>
    nestedAssetVisitPresenterDataProps(displayMode.value, {
      assetVisit: dropoffCarrier.value?.assetVisit ?? null
    })
  );
  const pickupAssetVisitPresenterProps = computed(() =>
    nestedAssetVisitPresenterDataProps(displayMode.value, {
      assetVisit: pickupCarrier.value?.assetVisit ?? null
    })
  );

  const {
    codeAttrs,
    typeAttrs,
    typeChipAttrs,
    typeChipTextAttrs,
    typeIconAttrs,
    arrivedTimeAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    statusIconAttrs,
    displayStatusAttrs,
    displayStatusChipAttrs,
    displayStatusChipTextAttrs,
    displayStatusIconAttrs,
    stateAttrs,
    stateChipAttrs,
    stateChipTextAttrs,
    tagChipAttrs,
    tagsContainerAttrs,
    stateIconAttrs
  } = useYardAssetDisplayAttrs(displaySize, options, {
    statusChipVariant,
    statusIcon,
    statusColor,
    statusLabel,
    displayStatusChipVariant,
    displayStatusIcon,
    displayStatusColor,
    displayStatusLabel,
    stateIcon
  });

  // Reactive shell so nested computed refs unwrap in templates and slot props when passed as a prop.
  return reactive({
    asset,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    code,
    typeRaw,
    typeLabel,
    arrivedTime,
    arrivedTimeFormatted,
    owner,
    status,
    statusLabel,
    displayStatus,
    displayStatusLabel,
    isInTransit,
    state,
    stateLabel,
    tags,
    statusIcon,
    statusColor,
    displayStatusIcon,
    displayStatusColor,
    stateIcon,
    spotId,
    dropoffCarrier,
    pickupCarrier,
    hasAssignment,
    statusChipVariant,
    displayStatusChipVariant,
    spotIcon,
    codeAttrs,
    typeAttrs,
    typeChipAttrs,
    typeChipTextAttrs,
    typeIconAttrs,
    arrivedTimeAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    statusIconAttrs,
    displayStatusAttrs,
    displayStatusChipAttrs,
    displayStatusChipTextAttrs,
    displayStatusIconAttrs,
    stateAttrs,
    stateChipAttrs,
    stateChipTextAttrs,
    tagChipAttrs,
    tagsContainerAttrs,
    stateIconAttrs,
    ownerPresenterProps,
    spotPresenterProps: spotPresenterPropsMerged,
    dropoffAppointmentPresenterProps,
    pickupAppointmentPresenterProps,
    dropoffAssetVisitPresenterProps,
    pickupAssetVisitPresenterProps
  });
}

export type YardAssetDisplayData = ReturnType<typeof useYardAssetDisplay>;

/** @deprecated Use {@link YardAssetDisplayData} */
export type YardAssetDisplayState = YardAssetDisplayData;
